import { z } from 'zod';

// GET /pcmp/proposals/:id (API LB-743). Amounts are numbers rounded to 2
// decimals; day fields are 'YYYY-MM-DD', timestamps ISO strings.

export const PCMP_CHANNELS = ['ACCOUNTS', 'MARKETING'] as const;
export type PcmpChannel = (typeof PCMP_CHANNELS)[number];

export const PCMP_PAYMENT_TYPES = [
  'BKASH',
  'NAGAD',
  'BANK',
  'ROCKET',
  'UPAY',
  'OTHERS',
  'CASH',
  'GIFT',
] as const;
export type PcmpPaymentType = (typeof PCMP_PAYMENT_TYPES)[number];

/** When an Accounts payment failed: before or after the money went out. */
export const PCMP_FAILURE_STAGES = ['PRE_PAYOUT', 'POST_PAYOUT'] as const;
export type PcmpFailureStage = (typeof PCMP_FAILURE_STAGES)[number];

/** Where a hold parks a proposal (API PcmpHoldStage). */
export const PCMP_HOLD_STAGES = ['MARKETING', 'MIS', 'ACCOUNTS', 'MKT_DISBURSE'] as const;
export type PcmpHoldStage = (typeof PCMP_HOLD_STAGES)[number];

/** Where an admin reject caught the proposal (API PcmpRejectedAtStage). */
export const PCMP_REJECTED_AT_STAGES = [
  'MARKETING',
  'MIS',
  'PRE_BATCH',
  'ROUTE_BATCH',
  'ACCOUNTS',
  'ACC_CORRECTION',
  'MKT_DISBURSE',
] as const;
export type PcmpRejectedAtStage = (typeof PCMP_REJECTED_AT_STAGES)[number];

export const PCMP_ACCOUNT_KINDS = ['BANK', 'MFS'] as const;
export type PcmpAccountKind = (typeof PCMP_ACCOUNT_KINDS)[number];

export const PCMP_LANE_TONES = ['DONE', 'PROGRESS', 'HOLD', 'REJECTED', 'NONE'] as const;
export type PcmpLaneTone = (typeof PCMP_LANE_TONES)[number];

export const PCMP_TIMELINE_STATUSES = [
  'COMPLETED',
  'REJECTED',
  'ONHOLD',
  'RELEASED',
  'EDITED',
  'PENDING',
  'WAITING',
  'OPTIONAL',
] as const;
export type PcmpTimelineStatus = (typeof PCMP_TIMELINE_STATUSES)[number];

export const PCMP_EDITOR_ROLES = [
  'POST_DISBURSEMENT',
  'ANY_STAGE',
  'MARKETING',
  'MIS',
  'CORRECTION_REQUEST',
] as const;
export type PcmpEditorRole = (typeof PCMP_EDITOR_ROLES)[number];

/** How a Marketing approval counts: a chain level, the admin fallback, or an extra endorsement. */
export const PCMP_MKT_APPROVAL_KINDS = ['LEVEL', 'ADMIN_FALLBACK', 'ENDORSE'] as const;
export type PcmpMktApprovalKind = (typeof PCMP_MKT_APPROVAL_KINDS)[number];

export const PCMP_MKT_REJECTION_TYPES = ['TEACHER', 'SCHOOL_WITH_TEACHER'] as const;
export type PcmpMktRejectionType = (typeof PCMP_MKT_REJECTION_TYPES)[number];

export const PCMP_MKT_SCHOOL_REJECT_REASONS = [
  'SCHOOL_NOT_ENLISTED',
  'INFORMATION_CORRECTION',
] as const;
export type PcmpMktSchoolRejectReason = (typeof PCMP_MKT_SCHOOL_REJECT_REASONS)[number];

const idSchema = z.coerce.number();

const resolvedStatusSchema = z.object({ code: z.string(), label: z.string() });

const laneCellSchema = z.object({
  tone: z.enum(PCMP_LANE_TONES),
  text: z.string().nullable(),
  sub: z.string().nullable(),
});

export type PcmpLaneCell = z.infer<typeof laneCellSchema>;

export const pcmpStatusViewSchema = z.object({
  channel: z.enum(PCMP_CHANNELS).nullable(),
  status: resolvedStatusSchema,
  lanes: z.object({
    marketing: laneCellSchema,
    mis: laneCellSchema,
    accounts: laneCellSchema,
    overall: laneCellSchema,
  }),
});

export type PcmpStatusView = z.infer<typeof pcmpStatusViewSchema>;

/** The MO's confirmation of an Accounts payout (LDS-747) and MIS's verification of any payout (LDS-770). */
export const PCMP_DISBURSEMENT_CONFIRM_STATUSES = ['PENDING', 'CONFIRMED'] as const;
export type PcmpDisbursementConfirmStatus = (typeof PCMP_DISBURSEMENT_CONFIRM_STATUSES)[number];

export const PCMP_DISBURSEMENT_VERIFY_STATUSES = ['PENDING', 'VERIFIED'] as const;
export type PcmpDisbursementVerifyStatus = (typeof PCMP_DISBURSEMENT_VERIFY_STATUSES)[number];

const payoutAckShape = {
  at: z.string().nullable(),
  byUserId: idSchema.nullable(),
  remarks: z.string().nullable(),
};

/** The two acknowledgements of the live payout (LB-1195); each null where it does not apply. */
export const pcmpPayoutAcksSchema = z.object({
  /** Accounts payouts only. */
  confirmation: z
    .object({ status: z.enum(PCMP_DISBURSEMENT_CONFIRM_STATUSES), ...payoutAckShape })
    .nullable(),
  /** Either channel. */
  verification: z
    .object({ status: z.enum(PCMP_DISBURSEMENT_VERIFY_STATUSES), ...payoutAckShape })
    .nullable(),
});

export type PcmpPayoutAcks = z.infer<typeof pcmpPayoutAcksSchema>;

const balanceShape = {
  agreed: z.number(),
  /** The institute is rejected for the project: agreed is kept but not in due/extra (LB-1200). */
  agreedRejected: z.boolean().default(false),
  disbursed: z.number(),
  inFlight: z.number(),
  due: z.number(),
  extra: z.number(),
};

const balanceSchema = z.object(balanceShape);

export type PcmpBalance = z.infer<typeof balanceSchema>;

const calculationSchema = z.object({
  ...balanceShape,
  submissionId: idSchema,
  companyId: idSchema,
  totalSalesTarget: z.number(),
  totalCmpPercent: z.number(),
  perStudentCost: z.number(),
  totalStudentQty: z.number(),
  cmpSalesTarget: z.number(),
  practiceSalesTarget: z.number(),
  practiceQty: z.number(),
  perStudentDivisor: z.number(),
  grammarCapPercent: z.number(),
  categoryTargets: z.array(
    z.object({
      categoryId: idSchema,
      categoryCode: z.string(),
      categoryName: z.string(),
      tentativeQty: z.number(),
      salesTarget: z.number(),
      purchasePercent: z.number(),
    }),
  ),
  categorySplit: z
    .object({
      grammar: z.number(),
      practice: z.number(),
      grammarPercent: z.number(),
      practicePercent: z.number(),
    })
    .nullable(),
});

export type PcmpSubmissionCalculation = z.infer<typeof calculationSchema>;

export const pcmpPaymentAccountSchema = z.object({
  id: idSchema,
  kind: z.enum(PCMP_ACCOUNT_KINDS),
  bankName: z.string().nullable(),
  branchName: z.string().nullable(),
  accountNo: z.string().nullable(),
  routingNo: z.string().nullable(),
  mobileNo: z.string().nullable(),
  holderName: z.string().nullable(),
});

export type PcmpPaymentAccount = z.infer<typeof pcmpPaymentAccountSchema>;

const studentClassSchema = z.object({
  classId: idSchema,
  className: z.string().nullable(),
  studentQty: z.number(),
  books: z.array(
    z.object({
      projectBookId: idSchema,
      categoryId: idSchema,
      categoryCode: z.string(),
      categoryName: z.string(),
      bookPrice: z.number(),
      tentativePurchaseQty: z.number(),
    }),
  ),
});

export type PcmpProposalStudentClass = z.infer<typeof studentClassSchema>;

const ledgerItemSchema = z.object({
  proposalId: idSchema,
  refNo: z.string(),
  proposalDate: z.string(),
  proposalType: z.string(),
  proposedAmount: z.number(),
  paidAmount: z.number(),
  overallStatus: z.string(),
  status: resolvedStatusSchema,
  isCurrent: z.boolean(),
});

const teacherSchema = z.object({
  submissionTeacherId: idSchema,
  teacherId: idSchema.nullable(),
  name: z.string(),
  mobile: z.string().nullable(),
  designation: z.string().nullable(),
  subject: z.string().nullable(),
  agreedAmount: z.number(),
  comments: z.string().nullable(),
  isDeleted: z.boolean(),
  balance: balanceSchema.nullable(),
  categoryAmounts: z.array(
    z.object({
      categoryId: idSchema,
      categoryCode: z.string(),
      categoryName: z.string(),
      amount: z.number(),
    }),
  ),
  ledger: z.array(ledgerItemSchema),
});

export type PcmpProposalTeacher = z.infer<typeof teacherSchema>;

const paymentSchema = z.object({
  paymentType: z.enum(PCMP_PAYMENT_TYPES),
  proposedAmount: z.number(),
  /** Paid so far, net of what partial returns took back. */
  paidAmount: z.number(),
  /** Taken back from the live payment by partial returns (LDS-765). */
  returnedAmount: z.number(),
  /** What a return can still take back; 0 when nothing is paid. */
  returnableAmount: z.number(),
  paymentDate: z.string().nullable(),
  referenceNo: z.string().nullable(),
  isOnBehalf: z.boolean(),
  onBehalfName: z.string().nullable(),
  comments: z.string().nullable(),
  account: pcmpPaymentAccountSchema.nullable(),
});

export type PcmpProposalPayment = z.infer<typeof paymentSchema>;

const instituteProposalSchema = z.object({
  proposalId: idSchema,
  refNo: z.string(),
  proposalDate: z.string(),
  status: resolvedStatusSchema,
  overallStatus: z.string(),
  isOnHold: z.boolean(),
  holdStage: z.string().nullable(),
  projectId: idSchema,
  projectName: z.string(),
  teacherName: z.string(),
  teacherMobile: z.string().nullable(),
  teacherDesignation: z.string().nullable(),
  proposedAmount: z.number(),
  paidAmount: z.number(),
});

export type PcmpInstituteProposal = z.infer<typeof instituteProposalSchema>;

const timelineEntrySchema = z.object({
  eventId: idSchema,
  eventType: z.string(),
  userName: z.string().nullable(),
  designationName: z.string().nullable(),
  verb: z.string(),
  status: z.enum(PCMP_TIMELINE_STATUSES),
  pill: z.string().nullable(),
  comment: z.string().nullable(),
  changes: z.record(z.string(), z.unknown()).nullable(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  occurredAt: z.string(),
  isLatest: z.boolean(),
});

export type PcmpTimelineEntry = z.infer<typeof timelineEntrySchema>;

const timelineNodeSchema = z.object({
  kind: z.string(),
  title: z.string(),
  level: z.number().nullable(),
  status: z.enum(PCMP_TIMELINE_STATUSES),
  userId: idSchema.nullable(),
  userName: z.string().nullable(),
  designationName: z.string().nullable(),
  mobile: z.string().nullable(),
  occurredAt: z.string().nullable(),
  comment: z.string().nullable(),
  subStatusLabel: z.string().nullable(),
  isCurrentUser: z.boolean(),
  isFallback: z.boolean(),
  isOptional: z.boolean(),
  isInactiveUser: z.boolean(),
  history: z.array(timelineEntrySchema),
});

export type PcmpTimelineNode = z.infer<typeof timelineNodeSchema>;

const activityCardSchema = z.object({
  userId: idSchema.nullable(),
  userName: z.string().nullable(),
  designationName: z.string().nullable(),
  isCurrentUser: z.boolean(),
  kind: z.string(),
  status: z.enum(PCMP_TIMELINE_STATUSES),
  verb: z.string(),
  title: z.string(),
  pill: z.string().nullable(),
  level: z.number(),
  occurredAt: z.string(),
  comment: z.string().nullable(),
  entries: z.array(timelineEntrySchema),
});

export type PcmpActivityCard = z.infer<typeof activityCardSchema>;

export const pcmpTimelineSchema = z.object({
  isFallbackMode: z.boolean(),
  isSubmitterInactive: z.boolean(),
  mo: timelineNodeSchema,
  marketingChain: z.array(timelineNodeSchema),
  adminApprovers: z.array(timelineNodeSchema),
  mis: timelineNodeSchema,
  disbursement: timelineNodeSchema,
  activity: z.array(activityCardSchema),
});

export type PcmpTimeline = z.infer<typeof pcmpTimelineSchema>;

const disbursementProofSchema = z.object({
  attachmentId: idSchema,
  /** Null when the amount was zero (no ledger row). */
  disbursementId: idSchema.nullable(),
  /** A signed, short-lived link. */
  url: z.string().nullable(),
  contentType: z.string().nullable(),
  isPdf: z.boolean(),
  sizeBytes: z.number().nullable(),
  uploadedAt: z.string(),
  uploadedBy: z.object({ id: idSchema, name: z.string().nullable() }).nullable(),
});

export type PcmpDisbursementProof = z.infer<typeof disbursementProofSchema>;

const capabilitiesSchema = z.object({
  canEdit: z.boolean(),
  editorRole: z.enum(PCMP_EDITOR_ROLES).nullable(),
  editLane: z.string().nullable(),
  isPostDisbursementEdit: z.boolean(),
  editBlockedReason: z.string().nullable(),
  canChangePaymentSource: z.boolean(),
  paymentSourceBlockedReason: z.string().nullable(),
  canMktApprove: z.boolean(),
  mktApproveKind: z.enum(PCMP_MKT_APPROVAL_KINDS).nullable(),
  mktApproveLevelNo: z.number().nullable(),
  mktApproveBlockedReason: z.string().nullable(),
  canMktReject: z.boolean(),
  /** A School with Teacher reject is open: the submission has only this proposal. */
  canRejectInstitute: z.boolean(),
  mktRejectBlockedReason: z.string().nullable(),
  canSubmitMktExplanation: z.boolean(),
  mktExplanationBlockedReason: z.string().nullable(),
  canMisDecide: z.boolean(),
  misDecisionBlockedReason: z.string().nullable(),
  /** Accounts may mark it Disbursement Failed, at the stage it would fail. */
  canAccountsFail: z.boolean(),
  accountsFailureStage: z.enum(PCMP_FAILURE_STAGES).nullable(),
  accountsFailBlockedReason: z.string().nullable(),
  /** Admin / MIS may take an Accounts payout back, all or part (LDS-765). */
  canAccountsReturn: z.boolean(),
  accountsReturnBlockedReason: z.string().nullable(),
  /** Admin / MIS may take a Marketing payout back, all or part (LDS-766). */
  canMarketingReturn: z.boolean(),
  marketingReturnBlockedReason: z.string().nullable(),
  /** The viewer is the submitting MO of an unconfirmed Accounts payout (LB-1195). */
  canConfirmDisbursement: z.boolean(),
  confirmDisbursementBlockedReason: z.string().nullable(),
  /** MIS / Admin may verify its payout, either channel (LB-1195). */
  canVerifyDisbursement: z.boolean(),
  verifyDisbursementBlockedReason: z.string().nullable(),
  /** The chain or an admin may correct the failed payment and approve it (LB-764). */
  canCorrect: z.boolean(),
  correctionBlockedReason: z.string().nullable(),
  /** Marketing channel: uploading the payment proof is the payout (LB-768). */
  canMktDisburse: z.boolean(),
  mktDisburseBlockedReason: z.string().nullable(),
  /** Whoever can act at its stage may hold it (LB-772); holdStage is where it would park. */
  canHold: z.boolean(),
  holdStage: z.enum(PCMP_HOLD_STAGES).nullable(),
  holdBlockedReason: z.string().nullable(),
  /** The holder, an actor at the held stage or an admin may release it. */
  canRelease: z.boolean(),
  releaseBlockedReason: z.string().nullable(),
  /** A PCMP admin may reject it for good (LB-773), from the stage it would be rejected at. */
  canAdminReject: z.boolean(),
  adminRejectStage: z.enum(PCMP_REJECTED_AT_STAGES).nullable(),
  adminRejectBlockedReason: z.string().nullable(),
  /** The viewer may raise a priority request on it (LB-777), or why not. */
  canRequest: z.boolean(),
  requestBlockedReason: z.string().nullable(),
  /** An open request exists and the viewer may process, approve or reject it (LB-778). */
  canActOnRequest: z.boolean(),
  /** Why approving it would be refused now; null when it can run or only records. */
  requestApproveBlockedReason: z.string().nullable(),
});

export type PcmpProposalCapabilities = z.infer<typeof capabilitiesSchema>;

const batchShape = { batchId: idSchema, batchNo: z.number(), status: z.string() };

export const pcmpProposalDetailSchema = z.object({
  proposalId: idSchema,
  refNo: z.string(),
  proposalDate: z.string(),
  proposalType: z.string(),
  challanNo: z.string().nullable(),
  project: z.object({
    id: idSchema,
    displayName: z.string(),
    companyId: idSchema,
    isActive: z.boolean(),
    isWebVisible: z.boolean(),
    activeFrom: z.string().nullable(),
    activeTo: z.string().nullable(),
  }),
  status: pcmpStatusViewSchema,
  payout: pcmpPayoutAcksSchema,
  overallStatus: z.string(),
  paymentSource: z.enum(PCMP_CHANNELS),
  hold: z
    .object({
      holdId: idSchema,
      stage: z.string(),
      stageLabel: z.string(),
      reason: z.string().nullable(),
      heldAt: z.string(),
      heldByUserId: idSchema.nullable(),
    })
    .nullable(),
  /** The latest admin reject; null when no admin rejected it. */
  adminRejection: z
    .object({
      stage: z.enum(PCMP_REJECTED_AT_STAGES),
      stageLabel: z.string(),
      comment: z.string().nullable(),
      at: z.string(),
      by: z.object({ id: idSchema, name: z.string().nullable() }).nullable(),
    })
    .nullable(),
  routeBatch: z.object({ ...batchShape, channel: z.enum(PCMP_CHANNELS).nullable() }).nullable(),
  urgentBatch: z.object(batchShape).nullable(),
  openRequest: z
    .object({
      requestId: idSchema,
      categoryId: idSchema,
      categoryCode: z.string(),
      categoryName: z.string(),
      status: z.string(),
      note: z.string().nullable(),
      raisedAt: z.string(),
      raisedByUserId: idSchema.nullable(),
    })
    .nullable(),
  mo: z.object({
    userId: idSchema,
    fullName: z.string().nullable(),
    hrEmpId: z.string().nullable(),
    mobile: z.string().nullable(),
    designationName: z.string().nullable(),
    thanaName: z.string().nullable(),
    districtName: z.string().nullable(),
    pcmpDivision: z.string(),
    isActive: z.boolean(),
  }),
  institute: z.object({
    id: idSchema,
    name: z.string().nullable(),
    eiinNumber: z.string().nullable(),
    thanaName: z.string().nullable(),
    districtName: z.string().nullable(),
    isDeleted: z.boolean(),
    submissionId: idSchema,
    submissionStatus: z.string(),
    submissionComments: z.string().nullable(),
    submittedAt: z.string(),
  }),
  calculation: calculationSchema,
  students: z.array(studentClassSchema),
  teacher: teacherSchema,
  payment: paymentSchema,
  /** Mobile proofs only; the Marketing payment proof is `disbursementProof`. */
  attachments: z.array(
    z.object({
      id: idSchema,
      kind: z.string(),
      url: z.string().nullable(),
      contentType: z.string().nullable(),
    }),
  ),
  /** The Marketing payment proof; null until uploaded (and for migrated proposals). */
  disbursementProof: disbursementProofSchema.nullable(),
  instituteProposals: z.array(instituteProposalSchema),
  timeline: pcmpTimelineSchema,
  capabilities: capabilitiesSchema,
});

export type PcmpProposalDetail = z.infer<typeof pcmpProposalDetailSchema>;

// Section edits and the payment source change (API LB-744/745). A field
// left out keeps the saved value; null clears it.

export type PcmpProposalEditSection = 'institute' | 'students' | 'teacher' | 'payment';

interface PcmpEditReasonPayload {
  reason: string;
}

export interface PcmpInstituteEditPayload extends PcmpEditReasonPayload {
  instituteId: number;
  comments: string | null;
}

export interface PcmpStudentsEditPayload extends PcmpEditReasonPayload {
  classes?: { classId: number; studentQty: number }[];
  books?: { projectBookId: number; tentativePurchaseQty: number }[];
}

export interface PcmpTeacherEditPayload extends PcmpEditReasonPayload {
  name: string;
  mobile: string;
  designation: string | null;
  subject: string | null;
  agreedAmount: number;
  proposedAmount: number;
  comments: string | null;
}

export interface PcmpAccountEditPayload {
  bankName?: string | null;
  branchName?: string | null;
  accountNo?: string | null;
  routingNo?: string | null;
  mobileNo?: string | null;
  holderName?: string | null;
}

export interface PcmpPaymentEditPayload extends PcmpEditReasonPayload {
  /** Another payment type (LDS-791); left out keeps it. */
  paymentType?: PcmpPaymentType;
  account?: PcmpAccountEditPayload;
  isOnBehalf: boolean;
  onBehalfName?: string | null;
  /** YYYY-MM-DD, or null to clear. */
  paymentDate: string | null;
  referenceNo: string | null;
}

export interface PcmpProposalEditPayloads {
  institute: PcmpInstituteEditPayload;
  students: PcmpStudentsEditPayload;
  teacher: PcmpTeacherEditPayload;
  payment: PcmpPaymentEditPayload;
}

export interface PcmpPaymentSourcePayload extends PcmpEditReasonPayload {
  paymentSource: PcmpChannel;
}

export const pcmpProposalEditResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  changes: z.record(z.string(), z.unknown()),
  /** Proposals whose shown data the edit changed, this one included. */
  affectedProposalIds: z.array(idSchema),
});

export type PcmpProposalEditResult = z.infer<typeof pcmpProposalEditResultSchema>;

export interface PcmpMktApprovePayload {
  comment?: string | undefined;
}

export interface PcmpMktRejectPayload {
  type: PcmpMktRejectionType;
  schoolReason?: PcmpMktSchoolRejectReason | undefined;
  comment: string;
}

export interface PcmpMktExplanationPayload {
  explanation: string;
}

export const pcmpMktDecisionResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  kind: z.enum(PCMP_MKT_APPROVAL_KINDS).nullable(),
  levelNo: z.number().nullable(),
  mktStatus: z.string(),
  overallStatus: z.string(),
  /** A School with Teacher reject closed the institute's submission. */
  submissionRejected: z.boolean(),
});

export type PcmpMktDecisionResult = z.infer<typeof pcmpMktDecisionResultSchema>;

export const pcmpMktExplanationResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
});

export type PcmpMktExplanationResult = z.infer<typeof pcmpMktExplanationResultSchema>;

export const PCMP_MIS_DECISIONS = ['VERIFIED', 'NOT_VERIFIED'] as const;
export type PcmpMisDecision = (typeof PCMP_MIS_DECISIONS)[number];

export interface PcmpMisDecisionPayload {
  decision: PcmpMisDecision;
  /** An MIS reason code; required for Not Verified, not sent for Verify. */
  reason?: string | undefined;
  comment: string;
}

export const pcmpMisDecisionResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  misStatus: z.string(),
  misReason: z.string().nullable(),
  overallStatus: z.string(),
});

export type PcmpMisDecisionResult = z.infer<typeof pcmpMisDecisionResultSchema>;

/** Disbursement Failed of one proposal; the reason is required. */
export interface PcmpAccountsReasonPayload {
  reason: string;
}

/**
 * Return of a payout, all or part (LDS-765 / 766). No amount takes back
 * everything left. Accounts needs the reason and the deposit slip; Marketing
 * takes both as optional.
 */
export interface PcmpReturnPaymentPayload {
  reason?: string | undefined;
  amount?: number | undefined;
  slipKey?: string | undefined;
}

const returnOutcomeShape = {
  /** This return's amount. */
  returnedAmount: z.number(),
  /** All returned from the payment so far. */
  totalReturned: z.number(),
  /** Still with the teacher; 0 once closed. */
  remaining: z.number(),
  /** The returns add up to the payment: the proposal is closed. */
  closed: z.boolean(),
  attachmentId: idSchema.nullable(),
};

export const pcmpAccountsPaymentResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  disbursementId: idSchema,
  accountsStatus: z.string(),
  /** Null for a return. */
  failureStage: z.enum(PCMP_FAILURE_STAGES).nullable(),
  overallStatus: z.string(),
});

export type PcmpAccountsPaymentResult = z.infer<typeof pcmpAccountsPaymentResultSchema>;

export const pcmpAccountsReturnResultSchema =
  pcmpAccountsPaymentResultSchema.extend(returnOutcomeShape);

export const pcmpMktReturnResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  disbursementId: idSchema,
  mktDisburseStatus: z.string(),
  overallStatus: z.string(),
  ...returnOutcomeShape,
});

/** What either Return answers; `closed` tells a full return from a partial one. */
export type PcmpReturnPaymentResult =
  | z.infer<typeof pcmpMktReturnResultSchema>
  | z.infer<typeof pcmpAccountsReturnResultSchema>;

/** Marketing disbursement: the uploaded proof's key and the comment, both required. */
export interface PcmpMktDisbursementPayload {
  proofKey: string;
  comment: string;
}

export const pcmpMktDisbursementResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  /** Null when the amount is zero (no ledger row). */
  disbursementId: idSchema.nullable(),
  attachmentId: idSchema,
  mktDisburseStatus: z.string(),
  overallStatus: z.string(),
});

export type PcmpMktDisbursementResult = z.infer<typeof pcmpMktDisbursementResultSchema>;

/** Hold needs a reason, release a comment (LB-772). */
export interface PcmpHoldPayload {
  reason: string;
}

export interface PcmpReleasePayload {
  comment: string;
}

export const pcmpHoldResultSchema = z.object({
  proposalId: idSchema,
  holdId: idSchema,
  eventId: idSchema,
  /** The stage it is (or was) parked at. */
  stage: z.enum(PCMP_HOLD_STAGES),
  stageLabel: z.string(),
  isOnHold: z.boolean(),
});

export type PcmpHoldResult = z.infer<typeof pcmpHoldResultSchema>;

/** Admin reject: the reason is required (LB-773). */
export interface PcmpAdminRejectPayload {
  reason: string;
}

export const pcmpAdminRejectResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  rejectedAtStage: z.enum(PCMP_REJECTED_AT_STAGES),
  rejectedAtStageLabel: z.string(),
  /** The route, urgent or re-disbursement batch it was pulled from. */
  removedFromBatchNo: z.number().nullable(),
  overallStatus: z.string(),
});

export type PcmpAdminRejectResult = z.infer<typeof pcmpAdminRejectResultSchema>;

/** The MO confirming an Accounts payout; remarks are optional (LB-1195). */
export interface PcmpDisbursementConfirmPayload {
  remarks?: string | undefined;
}

export const pcmpDisbursementConfirmResultSchema = z.object({
  proposalId: idSchema,
  eventId: idSchema,
  disbursementId: idSchema,
});

export type PcmpDisbursementConfirmResult = z.infer<typeof pcmpDisbursementConfirmResultSchema>;
